/** Small, opaque forest-floor forms; shared geometry, no texture billboards. */
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { range, type Rng } from '@hyperlight/shared';
import { V, tube, shade, paint, blob } from './primitives';
import { paintTreeWind, windJoint } from './treeWind';
import type { C3 } from './types';

/** A folded, closed leaf. The ridge gives it a readable lit and shaded plane. */
function leaf(root: THREE.Vector3, tip: THREE.Vector3, width: number, color: C3): THREE.BufferGeometry {
  const along = tip.clone().sub(root).normalize();
  const across = V(-along.z, 0, along.x).normalize().multiplyScalar(width);
  const middle = root.clone().lerp(tip, .48), ridge = middle.clone().add(V(0, width * .35, 0));
  const left = middle.clone().add(across), right = middle.clone().sub(across);
  const p = [root,left,ridge, left,tip,ridge, tip,right,ridge, right,root,ridge, root,right,tip, root,tip,left];
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(p.flatMap(v => v.toArray()), 3));
  g.computeVertexNormals();
  return paint(g, color);
}

function merge(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const g = mergeGeometries(parts, false)!;
  parts.forEach(p => p.dispose());
  g.computeVertexNormals(); g.computeBoundingBox(); g.computeBoundingSphere();
  return g;
}

/** A broad, knee-low thicket. Arching shoots carry overlapping paired leaves
 * from the base outward; there is no exposed trunk or separate crown. */
export function buildShrub(rng: Rng, color: C3, coarse=false): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const phase = rng() * Math.PI * 2;
  for (let s = 0; s < 7; s++) {
    const a = phase + s * 2.39996 + range(rng,-.2,.2);
    const along = V(Math.cos(a),0,Math.sin(a)), across = V(-Math.sin(a),0,Math.cos(a));
    const reach = range(rng,.52,.83), rise = range(rng,.26,.44);
    const root = V(0,0,0);
    const knots = [root, along.clone().multiplyScalar(reach*.32).add(V(0,rise*.88,0)),
      along.clone().multiplyScalar(reach*.69).add(V(0,rise,0)),
      along.clone().multiplyScalar(reach).add(V(0,rise*.55,0))];
    const joint = windJoint(root,knots[2],.018,1.6);
    if(!coarse)for(let k=0;k<3;k++)parts.push(paintTreeWind(tube(knots[k],knots[k+1],.018-k*.005,.013-k*.005,[.17,.16,.09],3),joint));
    const at = (t:number) => {
      const k = Math.min(2,Math.floor(t*3));
      return knots[k].clone().lerp(knots[k+1],t*3-k);
    };
    for (let j = 0; j < 6; j++) {
      for(const side of [-1,1]) {
        const t = .12+j*.145+range(rng,-.025,.025), base = at(t);
        const tip = base.clone().addScaledVector(along,range(rng,.14,.25))
          .addScaledVector(across,side*range(rng,.15,.25)).add(V(0,range(rng,-.045,.06),0));
        const width=range(rng,.10,.145),tint=shade(color,range(rng,.76,1.08));
        // Consume identical randomness: coarse leaves keep the same shoots and
        // wind joints. Broader alternate pairs cover the small omitted leaves.
        if(!coarse||j%2===0)parts.push(paintTreeWind(leaf(base,tip,width*(coarse?1.3:1),tint),joint));
      }
    }
  }
  return merge(parts);
}

/** Dry canyon scrub: overlapping leafy forks start low on several connected
 * woody shoots. Small leaf edges break the silhouette of each faceted mass. */
export function buildCanyonShrub(rng:Rng,color:C3,coarse=false):THREE.BufferGeometry {
  const parts:THREE.BufferGeometry[]=[],phase=rng()*Math.PI*2;
  const bark:C3=[.22,.145,.075];
  for(let s=0;s<4;s++){
    const a=phase+s*2.39996+range(rng,-.35,.35),along=V(Math.cos(a),0,Math.sin(a)),across=V(-along.z,0,along.x);
    const h=range(rng,.85,1.45),reach=range(rng,.5,.95);
    const root=along.clone().multiplyScalar(range(rng,.04,.16)).add(V(0,-.035,0));
    const elbow=along.clone().multiplyScalar(reach*.28).add(V(0,h*.28,0));
    const tip=along.clone().multiplyScalar(reach*.75).add(V(0,h*.88,0));
    const joint=windJoint(root,tip,.046,1.15);
    parts.push(paintTreeWind(tube(root,elbow,.048,.030,bark,coarse?3:4),joint));
    parts.push(paintTreeWind(tube(elbow,tip,.030,.011,bark,coarse?3:4),joint));
    for(let b=0;b<3;b++){
      const side=b%2===0?-1:1,base=elbow.clone().lerp(tip,.04+b*.30);
      const end=base.clone().addScaledVector(across,side*range(rng,.22,.40)).addScaledVector(along,range(rng,.12,.25)).add(V(0,range(rng,.07,.16),0));
      if(!coarse)parts.push(paintTreeWind(tube(base,end,.018,.006,bark,3),joint));
      for(let j=0;j<2;j++){
        const center=base.clone().lerp(end,.44+j*.48),r=range(rng,.24,.32);
        // Consume the same plan in both LODs. A broad first mass replaces its
        // overlapping companion only once individual leaves are sub-pixel.
        const g=blob(rng,V(0,0,0),r,shade(color,range(rng,.83,1.10)),.18);
        g.scale(coarse?1.35:1.05,.86,coarse?1.35:1.05);g.translate(center.x,center.y,center.z);
        if(!coarse||j===0)parts.push(paintTreeWind(g,joint));else g.dispose();
      }
      for(const sign of[-1,1]){
        const at=base.clone().lerp(end,.6),leafTip=at.clone().addScaledVector(along,range(rng,.22,.34)).addScaledVector(across,sign*range(rng,.20,.31)).add(V(0,range(rng,-.05,.12),0));
        const width=range(rng,.09,.14),tint=shade(color,range(rng,.80,1.14));
        if(!coarse)parts.push(paintTreeWind(leaf(at,leafTip,width,tint),joint));
      }
    }
    const top=blob(rng,V(0,0,0),range(rng,.23,.29),shade(color,range(rng,.86,1.12)),.18);
    top.scale(1,.83,1);top.translate(tip.x,tip.y,tip.z);parts.push(paintTreeWind(top,joint));
  }
  return merge(parts);
}

/** Low, irregular moss cushions; underside buried by the support solve. */
export function buildMoss(rng: Rng, color: C3): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 7; i++) {
    const a = rng() * Math.PI * 2, r = Math.sqrt(rng()) * .35;
    const g = new THREE.IcosahedronGeometry(range(rng,.16,.30),0);
    g.scale(1,.28,range(rng,.8,1.35));
    g.translate(Math.cos(a)*r,.025,Math.sin(a)*r);
    parts.push(paint(g,shade(color,range(rng,.72,.96))));
  }
  return merge(parts);
}

/** A broken carpet of folded leaves, not an opaque decal over the terrain. */
export function buildLitter(rng: Rng): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 18; i++) {
    const a = rng() * Math.PI * 2, r = Math.sqrt(rng()) * .58, yaw = rng()*Math.PI*2;
    const root = V(Math.cos(a)*r,range(rng,.018,.045),Math.sin(a)*r);
    const len = range(rng,.10,.22);
    const tip = root.clone().add(V(Math.cos(yaw)*len,range(rng,0,.025),Math.sin(yaw)*len));
    parts.push(leaf(root,tip,len*.33,shade([.29,.21,.105],range(rng,.7,1.25))));
  }
  return merge(parts);
}
